/**
 * $computed expression schemas — for validating computed prop values in specs
 *
 * These schemas allow $computed expressions in prop positions. The LLM can
 * output { "$computed": "formatScore", "args": { "score": 7.2 } } in any
 * prop that accepts a string value.
 *
 * Task: [#1158.P1.5]
 * Agent: Diego <diego@liftping.net>
 */

import { z } from 'zod';

/** Valid DD computed function names */
export const DDComputedFunctionNames = [
  'formatScore',
  'formatCurrency',
  'tierCopy',
  'pluralize',
] as const;

export type DDComputedFunctionName = (typeof DDComputedFunctionNames)[number];

/**
 * Zod schema for a $computed expression node.
 *
 * Validates that:
 * - $computed references a known DD function name
 * - args is an optional record of argument values
 */
export const ComputedExpressionSchema = z.object({
  $computed: z.enum(DDComputedFunctionNames as unknown as [string, ...string[]]),
  args: z.record(z.string(), z.any()).optional(),
});

export type ComputedExpression = z.infer<typeof ComputedExpressionSchema>;

/**
 * Schema for a prop value that can be either a literal string or a $computed expression.
 * Use this in prop schemas where computed values are allowed.
 */
export const StringOrComputedSchema = z.union([
  z.string(),
  ComputedExpressionSchema,
]);
